import { Clipboard } from '@angular/cdk/clipboard';
import { Component, EventEmitter, NgZone, OnDestroy, Output } from '@angular/core';
import { buttonInOut } from 'animations-lib';
import { Observable, Subject, Subscription } from 'rxjs';
import { ISignatureFieldEvent, ISignatureFieldMeta, SignatureField } from '../../../interfaces/signature';
import { ActiveContentControlMonitor } from '../../../services/document-monitors/active-content-control-monitor';
import { ContentControlId } from '../../../services/document-monitors/content-control-id';
import { ContentControlToSignatureFieldService } from '../../../services/esignature/content-control-to-signature-field.service';
import { UidGenerator } from '../../../utils/uidGenerator';
import { IValidStateChangeProvider, ValidStateProvider } from '../../field-selection/valid-state-provider';
import { TooltipState } from '../../toolbar/tool-button';
import { DocusignOptionsService, PropertiesKeys } from './options/docusign-options.service';

@Component({
    selector: 'app-docusign',
    templateUrl: './docusign.component.html',
    styleUrls: [
        './docusign.component.scss',
        '../../field-selection/field-config/field-config.component.scss',
        '../../toolbar/tool-button.scss',
    ],
    animations: [buttonInOut],
    standalone: false
})
export class DocusignComponent implements OnDestroy, IValidStateChangeProvider {
  @Output()
  fieldClickedEvent = new EventEmitter<ISignatureFieldEvent>();

  recipientNo = 1;

  activeField: ({ contentControlId: number } & SignatureField) | undefined; // this is NOT a deep clone! so don't change it!
  viewState = {
    showFields: true,
    hasOptions: false,
    showOptions: false,
    showNoActions: false,
    showDataLabel: false,
    showChildFields: false,
  };

  docusignTabTypes = DocusignOptionsService.fields;

  private valid = new ValidStateProvider();
  private activeContentControlSubscription: Subscription;

  public tooltipState: TooltipState;
  private copyClicked = new Subject<void>();

  constructor(
    private activeContentControlMonitor: ActiveContentControlMonitor,
    private contentControlToSignatureFieldService: ContentControlToSignatureFieldService,
    private ngZone: NgZone,
    private clipboard: Clipboard
  ) {
    this.activeContentControlSubscription = activeContentControlMonitor.activeContentControlChanged$.subscribe({
      next: (acc) => this.activeControlChanged(acc),
    });
    this.tooltipState = new TooltipState(this.copyClicked.asObservable(), this.ngZone);
  }

  get validStateChange$(): Observable<boolean> {
    return this.valid.validStateChange$;
  }

  get dataLabel() {
    return this.activeField?.properties?.[PropertiesKeys.TAB_LABEL];
  }

  ngOnDestroy() {
    console.debug('DocusignComponent destroyed.');
    this.activeContentControlSubscription?.unsubscribe();
  }

  fieldAdded(field: ISignatureFieldMeta): void {
    const properties: any = {
      [PropertiesKeys.RECIPIENT_NO]: this.recipientNo,
      [PropertiesKeys.TAB_LABEL]: this.buildTabLabel(field),
    };

    // set group name of child field if current field is a parent
    if (this.activeField?.isParent) {
      properties[PropertiesKeys.GROUP_NAME] = this.activeField?.properties[PropertiesKeys.TAB_LABEL];
    }

    const signatureField: SignatureField = {
      ...field,
      id: this.recipientNo.toString(),
      properties: properties,
    };

    DocusignOptionsService.addDefaultValues(signatureField);

    this.fieldClickedEvent.emit({
      providerName: 'docusign',
      field: signatureField,
      contentControlId: undefined,
    });
  }

  closeOptions() {
    this.viewState.showOptions = false;
  }

  copyToClipboard() {
    this.clipboard.copy(this.dataLabel);
    this.copyClicked.next();
  }

  private isConditionalField(activeContentControl: ContentControlId): boolean {
    return activeContentControl.tag.startsWith('od:condition');
  }

  private isDocusignField(activeContentControl: ContentControlId): boolean {
    return activeContentControl.tag.startsWith('docusign-');
  }

  private activeControlChanged(activeContentControl: ContentControlId | undefined): void {
    console.debug('%cdocusign-component - active content control changed handler', 'color:#00aaaa');
    this.ngZone.run(() => {
      if (!activeContentControl || this.isConditionalField(activeContentControl)) {
        this.resetView();
        this.valid.setValid(true);
        return;
      }

      if (this.isDocusignField(activeContentControl)) {
        const field = this.contentControlToSignatureFieldService.get(activeContentControl.id);
        this.activeField = field ? { contentControlId: activeContentControl.id, ...field.field } : undefined;
        const fieldValid = this.activeField !== undefined;

        if (this.activeField?.id) {
          this.recipientNo = Number(this.activeField.id);
        }

        const hasChildFields = this.activeField?.isParent || false;
        this.docusignTabTypes = hasChildFields ? DocusignOptionsService.getChildren(this.activeField!.type) : DocusignOptionsService.fields;

        const hasOptions = fieldValid && DocusignOptionsService.hasOptions(this.activeField!.type);
        const showDataLabel = DocusignOptionsService.supportsDataLabel(this.activeField!.type);
        const showFields = !this.activeField || hasChildFields;
        const showNoActions = fieldValid && !hasOptions && !hasChildFields;

        this.viewState = {
          showFields: showFields,
          hasOptions: hasOptions,
          showOptions: hasOptions,
          showNoActions: showNoActions,
          showDataLabel: showDataLabel,
          showChildFields: hasChildFields,
        };
        this.valid.setValid(fieldValid);
        return;
      }

      this.resetView();
      this.valid.setValid(false);
    });
  }

  private resetView() {
    this.viewState = {
      showFields: true,
      hasOptions: false,
      showOptions: false,
      showNoActions: false,
      showChildFields: false,
      showDataLabel: false,
    };
    this.docusignTabTypes = DocusignOptionsService.fields;
    this.activeField = undefined;
  }

  private buildTabLabel(field: ISignatureFieldMeta): string {
    const parsedType = field.type.replaceAll(/\W/g, '_');
    return `${parsedType}_${UidGenerator.createGuid()}`;
  }

  showOptions(event: any) {
    this.viewState.showOptions = true;
    event.stopPropagation();
  }
}
